import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../axiosInterceptor';

type GoogleIntegrationAuthResult = {
  url: string;
  response: unknown;
};

type GoogleIntegrationAuthState = {
  loading: boolean;
  error: boolean;
  errorMessage: string;
  url: string;
  data: unknown | null;
};

const initialState: GoogleIntegrationAuthState = {
  loading: false,
  error: false,
  errorMessage: '',
  url: '',
  data: null,
};

const extractAuthUrl = (data: any): string => {
  const payload = data?.response ?? data ?? {};
  const candidate = payload?.url;
  return typeof candidate === 'string' ? candidate : '';
};

export const getGoogleIntegrationAuthUrl = createAsyncThunk<
  GoogleIntegrationAuthResult
>('getGoogleIntegrationAuthUrl', async () => {
  const { data } = await api.get('/api/integrations/google/auth');
  const url = extractAuthUrl(data);
  if (!url) {
    throw new Error('Missing Google auth URL.');
  }
  return {
    url,
    response: data?.response ?? data,
  };
});

const googleIntegrationAuthSlice = createSlice({
  name: 'googleIntegrationAuth',
  initialState,
  reducers: {
    resetGoogleIntegrationAuth: (state) => {
      state.loading = false;
      state.error = false;
      state.errorMessage = '';
      state.url = '';
      state.data = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getGoogleIntegrationAuthUrl.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = '';
      state.url = '';
    });
    builder.addCase(getGoogleIntegrationAuthUrl.fulfilled, (state, action) => {
      state.loading = false;
      state.url = action.payload.url;
      state.data = action.payload.response;
    });
    builder.addCase(getGoogleIntegrationAuthUrl.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.errorMessage =
        action.error.message ?? 'Failed to start Google integration.';
    });
  },
});

export const { resetGoogleIntegrationAuth } =
  googleIntegrationAuthSlice.actions;
export default googleIntegrationAuthSlice.reducer;
